import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  useTheme,
} from "@mui/material";

/**
 * Renders a radio group component.
 *
 * @component
 * @example
 * // Make sure options have label and value, i.e.
 * //
 * // const options = [
 * //   { label: 'Option 1', value: 'option1' },
 * //   { label: 'Option 2', value: 'option2' },
 * // ];
 *
 * @param {Object} props - The component props.
 * @param {string} [props.name=""] - The name of the radio group.
 * @param {string} [props.label=""] - The label for the radio group.
 * @param {string} [props.helperText=""] - The helper text for the radio group.
 * @param {Object} [props.rules={}] - The validation rules for the radio group.
 * @param {Array} [props.options=[]] - The options for the radio group.
 * @param {function} [props.handleChange] - The change event handler for the radio group.
 * @param {boolean} [props.disabled=false] - Whether the radio group is disabled.
 * @param {boolean} [props.row=true] - Whether the radio group should be displayed in a row.
 * @param {string} [props.radioSize="small"] - The size of the radio buttons.
 * @param {Object} [props.sx={}] - The custom styles for the radio group.
 * @returns {JSX.Element} The rendered component.
 */

export function MiRadioGroup({
  name = "",
  label = "",
  helperText = "",
  error,
  options = [],
  handleChange,
  disabled = false,
  row = true,
  value,
  radioSize = "small",
  sx = {},
}) {
  const theme = useTheme();

  return (
    <FormControl
      component="fieldset"
      data-testid={`mi-radio-group-${name}`}
      error={!!error}
      sx={{
        ...sx,
        "& > :first-of-type.Mui-focused": {
          color: theme.palette.common.black,
        },
      }}
    >
      <FormLabel
        sx={{
          color: theme.palette.common.black,
          fontWeight: theme.fontWeight.bold,
          fontSize: theme.fontSize.medium,
        }}
      >
        {label}
      </FormLabel>
      <FormHelperText sx={{ margin: "0 0 4px 0" }}>{helperText}</FormHelperText>
      <RadioGroup name={name} value={value} onChange={handleChange} row={row}>
        {options.map(({ label, value, disabled: optionDisabled = false }) => (
          <FormControlLabel
            key={`${label}${value}`}
            value={value}
            control={
              <Radio
                size={radioSize}
                inputProps={{
                  "data-testid": `mi-radio-group-${name}-${label}-${value}`,
                }}
              />
            }
            label={label}
            disabled={disabled || optionDisabled}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}
